import { motion, AnimatePresence } from 'framer-motion'
import { FastForward } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useTypingEffect } from '../../../hooks/useTypingEffect'
import { useTheme } from '../../../contexts/ThemeContext'
import contentService from '../../../services/contentService'

export const AboutSection = () => {
  const [aboutContent, setAboutContent] = useState('')
  const { displayedContent, setIsTyping, setDisplayedContent } = useTypingEffect(aboutContent)
  const [showSkipButton, setShowSkipButton] = useState(false)
  const { isDark } = useTheme()

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const { data } = await contentService.getByType('about')
        const contentText = data?.content || 'About content is not available yet.'
        setAboutContent(contentText)
      } catch (error) {
        console.error('Error fetching about content:', error)
        setAboutContent('Error loading content...')
      }
    }
    fetchAbout()
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setShowSkipButton(true), 2000)
    return () => clearTimeout(timer)
  }, [])

  const handleSkip = () => {
    setIsTyping(false)
    setDisplayedContent(aboutContent)
    setShowSkipButton(false)
  }

  return (
    <motion.div
      key="about"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="max-w-2xl mx-auto pb-16"
    >
      {/* Terminal card */}
      <div className={`
        rounded-xl overflow-hidden border-gradient
        ${isDark ? 'glass-futuristic' : 'glass-futuristic-light'}
      `}>
        {/* Terminal header */}
        <div className={`flex items-center gap-2 px-4 py-3 border-b ${isDark ? 'border-neutral-800' : 'border-neutral-100'}`}>
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-amber-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
          <span className={`ml-2 text-xs font-mono ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
            ~/about-me
          </span>
        </div>
        
        {/* Terminal content */}
        <div className="p-5 font-mono text-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-accent-500">$</span>
            <span className={isDark ? 'text-neutral-300' : 'text-neutral-700'}>cat about-me.md</span>
          </div>
          
          <div className={`
            whitespace-pre-line min-h-[200px] leading-relaxed
            ${isDark ? 'text-neutral-400' : 'text-neutral-600'}
          `}>
            {displayedContent}
            <motion.span 
              className={`inline-block w-1.5 h-4 ml-0.5 ${isDark ? 'bg-accent-400/60' : 'bg-accent-500/60'}`}
              animate={{ opacity: [1, 0, 1] }}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </div>
        </div>
      </div>

      {/* Skip button */}
      <AnimatePresence>
        {showSkipButton && displayedContent !== aboutContent && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-4 text-center"
          >
            <button
              onClick={handleSkip}
              className={`
                inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors
                ${isDark 
                  ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700' 
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }
              `}
            >
              <FastForward className="w-4 h-4" />
              Skip
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}